import { Divider, lighten, Stack, Typography } from '@mui/material';
import type { ObjectLiteral } from 'api/submission';
import { ExposuresWrapper } from 'components/ExposuresWrapper';
import { NumberFormatWithWrapper } from 'components/lib/NumberFormat';
import type { Field } from 'components/lib/QuestionEngine';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { useManualPremiumPerils } from 'hooks/useManualPremiumPerils';
import { type FC, Fragment, useEffect, useState } from 'react';

const WorkersCompensation: FC = () => {
  const {
    submission,
    relatedResource: employers,
    performAction,
  } = useExposuresOrGroupFields({
    type: 'exposure',
    name: 'employer',
  });

  const totalAnnualPayroll = submission?.data.uw_questions.payrollW2 || '0';

  const { mainPeril, manualPremiumPerils } = useManualPremiumPerils();

  const [state, setState] = useState<Record<string, string>>({});
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (manualPremiumPerils.length > 0) {
      let initialState = Object.fromEntries(
        manualPremiumPerils.map((peril) => [
          peril.data.man_class_code,
          peril.data.man_payroll_w2_user,
        ]),
      );

      if (mainPeril && !mainPeril.data.man_payroll_w2_user) {
        initialState[mainPeril.data.man_class_code] = totalAnnualPayroll;
      }

      // resets manualPremiumPerils values to default if totalAnnualPayroll value was changed
      const manualPremiumPerilsSum = Object.values(initialState).reduce(
        (acc: number, perilValue) => acc + Number(perilValue),
        0,
      );

      if (manualPremiumPerilsSum !== Number(totalAnnualPayroll)) {
        initialState = Object.fromEntries(
          Object.entries(initialState).map(([perilCode, _perilValue]) =>
            perilCode === mainPeril.data.man_class_code
              ? [perilCode, totalAnnualPayroll]
              : [perilCode, ''],
          ),
        );
      }

      setState(initialState);
    }
  }, [manualPremiumPerils, totalAnnualPayroll]);

  const handleChangeValue = (newValue: string, classCode: string) => {
    setState((prevState) => {
      const updatedState = { ...prevState, [classCode]: newValue };

      if (mainPeril) {
        const mainPerilCode = mainPeril.data.man_class_code;
        let totalAllocated = 0;

        for (const [code, value] of Object.entries(updatedState)) {
          if (code !== mainPerilCode) {
            totalAllocated += Number(value || 0);
          }
        }

        const remainingForMain = Math.max(0, Number(totalAnnualPayroll) - totalAllocated);
        updatedState[mainPerilCode] = remainingForMain.toString();
      }

      return updatedState;
    });
    setError(false);
  };

  const handleNext = async () => {
    const totalAllocated = Object.values(state).reduce((sum, value) => sum + Number(value), 0);

    if (Math.abs(totalAllocated - Number(totalAnnualPayroll)) > 0.01) {
      setError(true);
      return false;
    }

    const employer = employers?.[0];

    if (!employer) return true;

    // Check if the state is different from the backend state
    const isStateChanged = employer.perils
      ?.filter((peril) => peril.name === 'manual_premium')
      ?.some((peril) => {
        const perilCode = peril.data.man_class_code;
        const isMain = peril.data.man_ismain === 'Yes';
        const stateValue = state[perilCode];

        const payrollMainChanged =
          isMain &&
          peril.data.man_payroll_w2_user !== stateValue &&
          stateValue !== totalAnnualPayroll;

        const payrollNonMainChanged =
          !isMain &&
          (stateValue !== '' || peril.data.man_payroll_w2_user >= 0) &&
          !isNaN(Number(stateValue)) &&
          stateValue !== peril.data.man_payroll_w2_user;

        return payrollMainChanged || payrollNonMainChanged;
      });

    if (!isStateChanged) {
      return true;
    }

    await performAction({
      resourceId: employer.id,
      action: 'update',
      data: {
        ...(employer.data || {}),
        perils: employer.perils?.map((peril) => {
          const perilCode = peril.data.man_class_code;

          return {
            ...peril,
            data: {
              ...peril.data,
              man_payroll_w2_user: state[perilCode] || '0',
            },
          };
        }),
      },
      loadingText: 'Updating the payroll distribution',
    });

    return true;
  };

  const renderDutyNote = (isMain: boolean) => (
    <Typography
      variant="body2"
      component="span"
      sx={{
        display: { xs: 'block', sm: 'inline-block' },
        width: 'fit-content',
        padding: '.25rem .5rem',
        color: (theme) =>
          isMain ? theme.palette.customColors.gunMetal : theme.palette.customColors.success,
        backgroundColor: (theme) =>
          isMain
            ? theme.palette.customColors.lightBeige
            : lighten(`${theme.palette.customColors.success}`, 0.95),
        borderRadius: '4px',
      }}
    >
      {isMain ? 'Primary Duty' : 'Secondary Duty'}
    </Typography>
  );

  return (
    <ExposuresWrapper
      title="Employee Payroll Distribution"
      renderAddButton={false}
      onNext={handleNext}
    >
      <Stack gap={3}>
        <Stack>
          <Typography variant="h2" mb={1}>
            Please allocate payroll to secondary job duties, if applicable.
          </Typography>

          <Typography>Enter &apos;0&apos; if no employees perform that duty.</Typography>
        </Stack>

        {[
          ...manualPremiumPerils,
          {
            name: 'Total',
            data: {
              man_payroll_w2_user: totalAnnualPayroll,
            } as ObjectLiteral,
          },
        ].map((peril) => {
          const identifier = peril.data.man_class_code;
          const isTotalField = peril.name === 'Total';

          const splitedClassCodeDesc = peril?.data?.man_class_code_desc?.split(' - ') || [];
          const title = isTotalField ? 'Total sum' : `${splitedClassCodeDesc[0] ?? ''} Employees`;
          const titleIndustry = splitedClassCodeDesc[1] ?? '';

          const isMain = peril.data.man_ismain === 'Yes';
          const isFieldReadOnly = isTotalField || isMain;

          return (
            <Fragment key={title}>
              {isTotalField && <Divider />}

              <Stack
                sx={{ gap: 1.5, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    flex: 1,
                    width: { xs: '100%', sm: 'unset' },
                    ...(isTotalField && { color: (theme) => theme.palette.customColors.darkGray }),
                  }}
                >
                  {title}

                  {titleIndustry && <>&ensp;({titleIndustry})&ensp;</>}

                  {!isTotalField && renderDutyNote(isMain)}
                </Typography>

                <NumberFormatWithWrapper
                  sx={{
                    flex: '1',
                    width: { xs: '100%', sm: 'unset' },
                    ...(isTotalField && {
                      bgcolor: (theme) => theme.palette.customColors.lightBeige,
                    }),
                  }}
                  field={
                    isTotalField
                      ? ({} as Field)
                      : {
                          code: peril.data.man_class_code,
                        }
                  }
                  inputProps={{ prefix: '$' }}
                  isFieldReadOnly={isFieldReadOnly}
                  value={isTotalField ? peril.data.man_payroll_w2_user || '0' : state[identifier]}
                  placeholder="$0 (optional)"
                  onChange={(event) => handleChangeValue(event.target.value, identifier)}
                  error={error}
                />

                {isMain && error && (
                  <Typography color="error">Total allocation exceeds annual payroll</Typography>
                )}
              </Stack>
            </Fragment>
          );
        })}
      </Stack>
    </ExposuresWrapper>
  );
};

export default WorkersCompensation;
